import { ErrorCodes } from "@price-rescue/api-contracts";
import { POLICY_TARGET_MODE_PRODUCT, validatePriceMatchPolicy } from "@price-rescue/domain/policy";
import {
  PRODUCT_BULK_ACTIONS,
  PRODUCT_BULK_POLICY_LIMIT,
  PRODUCT_BULK_POLICY_REASONS,
} from "@price-rescue/domain/product-bulk-actions";
import { MerchantApiError } from "../authentication/errors.js";
import type { PoliciesService } from "./service.js";
import type { PriceMatchPolicy } from "./models.js";

export type ProductPolicyBulkAction =
  typeof PRODUCT_BULK_ACTIONS.POLICY_APPLY | typeof PRODUCT_BULK_ACTIONS.POLICY_CLEAR;
export interface ProductBulkPolicyInput {
  action: ProductPolicyBulkAction;
  productIds: readonly string[];
  rules?: Partial<PriceMatchPolicy>;
}
export interface ProductPolicyOutcome {
  productId: string;
  code: string;
  message: string;
}
export interface ProductBulkPolicyResult {
  updated: string[];
  failed: ProductPolicyOutcome[];
  skipped: ProductPolicyOutcome[];
  unconfirmed: ProductPolicyOutcome[];
}
export interface ProductBulkPolicyService {
  run(input: ProductBulkPolicyInput): Promise<ProductBulkPolicyResult>;
}
type PolicyApi = Pick<
  PoliciesService,
  "loadProductPolicy" | "saveProductOverride" | "deleteProductOverride"
>;

export function createProductBulkPolicyService(api: PolicyApi): ProductBulkPolicyService {
  return {
    async run(input) {
      const batch = freezeValidBatch(input);
      const result: ProductBulkPolicyResult = {
        updated: [],
        failed: [],
        skipped: [],
        unconfirmed: [],
      };
      let stopped = false;
      for (const productId of batch.productIds) {
        if (stopped) {
          result.skipped.push({
            productId,
            code: PRODUCT_BULK_POLICY_REASONS.NOT_STARTED,
            message: "Not started. Review previous results before trying again.",
          });
          continue;
        }
        stopped = await runProduct(api, batch, productId, result);
      }
      return result;
    },
  };
}

function freezeValidBatch(input: ProductBulkPolicyInput): ProductBulkPolicyInput {
  const productIds = [...new Set(input.productIds)];
  if (
    !productIds.length ||
    productIds.length > PRODUCT_BULK_POLICY_LIMIT ||
    productIds.some((id) => !id.trim())
  ) {
    throw new Error(
      `Select between 1 and ${PRODUCT_BULK_POLICY_LIMIT} distinct products from this page.`,
    );
  }
  const rules = { ...input.rules };
  if (input.action === PRODUCT_BULK_ACTIONS.POLICY_APPLY) {
    const errors = validatePriceMatchPolicy(rules);
    if (
      !Object.keys(rules).length ||
      Object.values(rules).some((value) => typeof value === "number" && !Number.isFinite(value)) ||
      errors.length
    ) {
      throw new Error(errors[0] ?? "Enter at least one valid override rule.");
    }
  }
  return { action: input.action, productIds, rules };
}

async function runProduct(
  api: PolicyApi,
  batch: ProductBulkPolicyInput,
  productId: string,
  result: ProductBulkPolicyResult,
): Promise<boolean> {
  let submitted = false;
  try {
    const current = await api.loadProductPolicy(productId);
    if (batch.action === PRODUCT_BULK_ACTIONS.POLICY_CLEAR && !current.configured) {
      result.skipped.push({
        productId,
        code: PRODUCT_BULK_POLICY_REASONS.UNCHANGED,
        message: "No product override to clear.",
      });
      return false;
    }
    submitted = true;
    if (batch.action === PRODUCT_BULK_ACTIONS.POLICY_APPLY) {
      await api.saveProductOverride({
        scope: POLICY_TARGET_MODE_PRODUCT,
        scopeId: productId,
        configured: true,
        enabled: true,
        policyOverride: { ...batch.rules },
      });
    } else {
      await api.deleteProductOverride(productId);
    }
    result.updated.push(productId);
    return false;
  } catch (error: unknown) {
    return recordFailure(error, submitted, productId, result);
  }
}

function recordFailure(
  error: unknown,
  submitted: boolean,
  productId: string,
  result: ProductBulkPolicyResult,
): boolean {
  const known = error instanceof MerchantApiError;
  // A catalog synchronization error may follow a saved policy. Only errors known
  // to precede persistence are confirmed rejections, even when retryable is false.
  const confirmedRejection =
    known &&
    (["auth", "access", "rate-limit"].includes(error.kind) ||
      error.code.startsWith("VALIDATION_") ||
      error.code === ErrorCodes.PRODUCT_NOT_ELIGIBLE);
  const uncertain = submitted && !confirmedRejection;
  if (uncertain) {
    result.unconfirmed.push({
      productId,
      code: PRODUCT_BULK_POLICY_REASONS.UNCONFIRMED,
      message:
        "The change may have saved. Review the product policy before retrying; no automatic retry was made.",
    });
  } else {
    result.failed.push({
      productId,
      code: known ? error.code : PRODUCT_BULK_POLICY_REASONS.FAILED,
      message: known
        ? error.message
        : "Could not read this product policy; no change was submitted.",
    });
  }
  return uncertain || (known && ["auth", "access", "rate-limit"].includes(error.kind));
}
